import type { Profile } from "@/domain/types";

// ---- Edennil: the maintainer's personal Deadlock setup, board-bound to the ReCore board's FPS layer
// authored bind-by-bind from his in-game screenshots (docs/deadlock-hotkeys/DL-1..6),
// never from the Default. Character-space: jump = Ctrl reads through the FPS layer onto the
// thumb whose keycode is Ctrl (the Space-coded thumb); crouch = Shift onto the Backspace-coded thumb;
// Open Shop = "[" onto the bracket key. Abilities 1-4 are the same keys for every hero.
// The thumb and spacebar stay distinct under active-code keying: jump = Ctrl lands on the
// thumb whose active FPS-layer code is ControlLeft, and the replay Switch teams = Space lands on the
// real spacebar (active code Space).
export const edennilProfile: Profile = {
  id: "edennil",
  name: "Edennil",
  pin: { keyboard: "recore-v3", layer: "recore-v3-layer-4" },
  base: [[]],
  contexts: {
    match: [
      // combat
      { binding: { name: "Fire", category: "combat" }, token: "MouseLeft" },
      { binding: { name: "Zoom", category: "combat", note: "Alt-fire" }, token: "MouseRight" },
      { binding: { name: "Reload", category: "combat" }, token: "Mouse5" },
      { binding: { name: "Melee", category: "combat" }, token: "Mouse4" },
      { binding: { name: "Parry", category: "combat", note: "Throw held item" }, token: "G" },
      // ability (1-4 are the same keys for every hero)
      { binding: { name: "Ability 1", category: "ability" }, token: "W" },
      { binding: { name: "Ability 2", category: "ability" }, token: "E" },
      { binding: { name: "Ability 3", category: "ability" }, token: "R" },
      { binding: { name: "Ability 4", category: "ability" }, token: "T" },
      { binding: { name: "Cancel Ability", category: "ability" }, token: "Q" },
      { binding: { name: "Alternate cast", category: "ability" }, token: "Alt" },
      // item
      { binding: { name: "Item 1", category: "item" }, token: "B" },
      { binding: { name: "Item 2", category: "item" }, token: "V" },
      { binding: { name: "Item 3", category: "item" }, token: "X" },
      { binding: { name: "Item 4", category: "item" }, token: "Z" },
      // movement
      { binding: { name: "Forward", category: "movement" }, token: "D" },
      { binding: { name: "Back", category: "movement" }, token: "C" },
      { binding: { name: "Strafe Left", category: "movement" }, token: "S" },
      { binding: { name: "Strafe Right", category: "movement" }, token: "F" },
      { binding: { name: "Dash", category: "movement" }, token: "A" },
      {
        binding: {
          name: "Jump",
          category: "movement",
          note: "Jump / mantle; zipline / rope; fly up (hero flight) shares this key",
        },
        token: "Ctrl",
      },
      {
        binding: { name: "Jump", category: "movement", note: "Alternate jump (scroll down)" },
        token: "WheelDown",
      },
      {
        binding: {
          name: "Crouch",
          category: "movement",
          note: "Crouch / slide; fly down (hero flight) shares this key",
        },
        token: "Shift",
      },
      // social / comms
      {
        binding: {
          name: "Ping",
          category: "social",
          note: "Tap to ping; hold for Chat Wheel",
        },
        token: "MouseMiddle",
      },
      { binding: { name: "Chat Wheel", category: "social" }, token: "Y" },
      {
        binding: { name: "Chat", category: "social", note: "Team chat (Alt+Enter for all)" },
        token: "Enter",
      },
      { binding: { name: "Push to Talk", category: "social", note: "Voice (team)" }, token: "8" },
      // interface (menu / HUD) - same category + colour as Destiny's Interface
      { binding: { name: "Open Shop", category: "interface" }, token: "[" },
      {
        binding: { name: "HUD detail view", category: "interface", note: "Hold; expands the HUD" },
        token: "Tab",
      },
      // camera (in-match teammate cameras on the number row; short "Cam N" cap + full readout note)
      { binding: { name: "Cam 1", category: "camera", note: "Teammate 1 camera" }, token: "1" },
      { binding: { name: "Cam 2", category: "camera", note: "Teammate 2 camera" }, token: "2" },
      { binding: { name: "Cam 3", category: "camera", note: "Teammate 3 camera" }, token: "3" },
      { binding: { name: "Cam 4", category: "camera", note: "Teammate 4 camera" }, token: "4" },
      { binding: { name: "Cam 5", category: "camera", note: "Teammate 5 camera" }, token: "5" },
    ],
    replay: [
      { binding: { name: "Switch teams", category: "spectator" }, token: "Space" },
      { binding: { name: "Camera mode", category: "spectator" }, token: "M" },
      {
        binding: { name: "Fly up", category: "spectator", note: "Free-cam / flight ascend" },
        token: "Ctrl",
      },
      {
        binding: { name: "Fly down", category: "spectator", note: "Free-cam / flight descend" },
        token: "Shift",
      },
      {
        binding: { name: "Spectate in-eye", category: "spectator", note: "First-person POV" },
        token: "WheelUp",
      },
      { binding: { name: "Spectate chase cam", category: "spectator" }, token: "WheelDown" },
      { binding: { name: "Toggle free cursor", category: "spectator" }, token: "J" },
      { binding: { name: "Dual-team minimap view", category: "spectator" }, token: "F1" },
      { binding: { name: "Target's minimap view", category: "spectator" }, token: "F2" },
      { binding: { name: "Amber minimap view", category: "spectator" }, token: "F3" },
      { binding: { name: "Sapphire minimap view", category: "spectator" }, token: "F4" },
      { binding: { name: "Increase replay speed", category: "spectator" }, token: "=" },
      { binding: { name: "Decrease replay speed", category: "spectator" }, token: "-" },
      { binding: { name: "Replay pause", category: "spectator" }, token: "P" },
    ],
  },
};
